import { createSelector, Selector } from "@ngxs/store"
import { COMPONENTS_STATE_TOKEN, ComponentsStateModel } from "./state"
import { Component, componentStatuses } from "@dis/models"

type StatusKey = keyof componentStatuses;
interface StatusFilter {
  statusKey: StatusKey,
  allowedStatusValues: string[];
}

export class ComponentsSelectors {

  /**
   *
   * @description Lazy version of the selector to work with signals
   * @param state
   * @returns
   */
  @Selector([COMPONENTS_STATE_TOKEN])
  static getById(state: ComponentsStateModel) {
    return (id: string) => {
      return state.items.find(item => item.uid === id);
    }
  }

  @Selector([COMPONENTS_STATE_TOKEN])
  static assemblies(state: ComponentsStateModel) {
    return state.items
      .filter(({ assemblyNumber }) => assemblyNumber)
  }

  static getByPartNumber(_partNumber: string) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ partNumber }) => partNumber === _partNumber)
    })
  }

  /** Assembly specific methods */
  static getAssembliesByInventoryRack(_rack: string) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ rack }) => rack === _rack);
    })
  }

  static getAssembliesByType(type: string) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ assemblyNumber, toolType }) => toolType === type && assemblyNumber)
    })
  }

  static getAssembliesByTypeAndFamily(type: string) {
    return createSelector([this.getAssembliesByType(type)], (components: Component[]) => {
      return components.sort((a, b) => a.inventoryStatus! < b.inventoryStatus! ? -1 : 1)
    })
  }

  static getAssembliesByTypeAndInventoryStatus(type: string) {
    return createSelector([this.getAssembliesByType(type)], (components: Component[]) => {
      return components.sort((a, b) => a.inventoryStatus! < b.inventoryStatus! ? -1 : 1)
    })
  }

  static getByPartNumberAndStatus(id: string, equalsOperatorBoolean: boolean, status: string) {
    return createSelector([this.getByPartNumber(id)], (components: Component[]) => {
      return components.filter(component => equalsOperatorBoolean
        ? component.inventoryStatus === status
        : component.inventoryStatus !== status
      )
    })
  }

  static getComponentsByOwner(owner: string) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ asset }) => asset?.owner?.includes(owner))
        .sort((a, b) => a.serialNumber! < b.serialNumber! ? 1 : -1)
    })
  }

  static getFilteredComponentsByMultipleSelectedStatuses(filters: {
    statusKey: StatusKey,
    allowedStatusValues: string[];
  }[]) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ statuses }) => filters.every(filter =>
        filter.allowedStatusValues.includes(statuses![filter.statusKey])
      ))
    })
  }

  static getOrderedComponents() {
    return createSelector([this.getFilteredComponentsByMultipleSelectedStatuses(
      [
        {
          statusKey: 'componentStatus',
          allowedStatusValues: ['ORDERED']
        }
      ]
    )], (components: Component[]) => {
      return components.sort((a, b) => {
        const valueA = a.serialNumber!.includes('LN') ? a.serialNumber!.replace(/^LN/, '').trim() : a.serialNumber!;
        const valueB = b.serialNumber!.includes('LN') ? b.serialNumber!.replace(/^LN/, '').trim() : b.serialNumber!;
        return valueA < valueB ? 1 : -1
      });
    })
  }

  static getCustomerKeptComponents() {
    return createSelector([this.getFilteredComponentsByMultipleSelectedStatuses(
      [
        {
          statusKey: 'componentStatus',
          allowedStatusValues: ['IN CUST POS']
        }
      ]
    )], (components: Component[]) => {
      return components.filter(component => component.assemblyNumber).sort((a, b) => {
        const valueA = a.serialNumber!.includes('LN') ? a.serialNumber!.replace(/^LN/, '').trim() : a.serialNumber!;
        const valueB = b.serialNumber!.includes('LN') ? b.serialNumber!.replace(/^LN/, '').trim() : b.serialNumber!;
        return valueA < valueB ? 1 : -1
      });
    })
  }

  static getDamagedComponents() {
    return createSelector([this.getFilteredComponentsByMultipleSelectedStatuses(
      [
        {
          statusKey: 'componentStatus',
          allowedStatusValues: ['DAMAGED']
        }
      ]
    )], (components: Component[]) => {
      return components.filter(component => component.assemblyNumber).sort((a, b) => {
        const valueA = a.serialNumber!.includes('LN') ? a.serialNumber!.replace(/^LN/, '').trim() : a.serialNumber!;
        const valueB = b.serialNumber!.includes('LN') ? b.serialNumber!.replace(/^LN/, '').trim() : b.serialNumber!;
        return valueA < valueB ? 1 : -1
      });
    })
  }

  static getByLatestAssignedBuild(_latestAssignedBuild: string) {
    return createSelector([COMPONENTS_STATE_TOKEN], (state: ComponentsStateModel) => {
      return state.items.filter(({ latestAssignedBuild }) => latestAssignedBuild === _latestAssignedBuild)
    })
  }


}
